import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import {
  ApiService,
  FeatureFlagOrgAssignment,
  FeatureFlagRegistryEntry,
  FeatureFlagScope,
  FeatureFlagUserAssignment,
  PlatformOrganizationItem,
  UserListItem,
} from '../../../shared/services/api.service';

type FeatureFlagsMode = 'org' | 'platform';
type FlagStateChoice = 'enabled' | 'disabled' | 'inherit';

type AssignmentRow = {
  scope: FeatureFlagScope;
  targetId: string;
  targetLabel: string;
  organizationLabel: string;
  enabled: boolean;
  updatedAt: string | Date;
};

@Component({
  selector: 'app-feature-flags',
  templateUrl: './feature-flags.component.html',
  styleUrls: ['./feature-flags.component.css'],
})
export class FeatureFlagsComponent implements OnInit {
  mode: FeatureFlagsMode = 'org';

  registry: FeatureFlagRegistryEntry[] = [];
  organizations: PlatformOrganizationItem[] = [];
  users: UserListItem[] = [];
  filteredUsers: UserListItem[] = [];

  selectedFlagKey = '';
  selectedScope: FeatureFlagScope = 'user';
  selectedTargetId = '';
  selectedOrgFilterId = '';
  selectedState: FlagStateChoice = 'enabled';
  userSearch = '';

  loadingRegistry = false;
  loadingAssignments = false;
  loadingTargets = false;
  applying = false;

  orgAssignments: FeatureFlagOrgAssignment[] = [];
  userAssignments: FeatureFlagUserAssignment[] = [];
  assignmentRows = new MatTableDataSource<AssignmentRow>([]);
  displayedColumns: string[] = ['scope', 'target', 'organization', 'state', 'updatedAt', 'actions'];

  confirmationMessage = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly apiService: ApiService,
    private readonly snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.mode = 'platform';
    this.updateConfirmationMessage();
    this.loadRegistry();
    this.loadOrganizations();
  }

  get isPlatformMode(): boolean {
    return this.mode === 'platform';
  }

  get selectedFlag(): FeatureFlagRegistryEntry | null {
    return this.registry.find((flag) => flag.key === this.selectedFlagKey) || null;
  }

  get availableScopes(): FeatureFlagScope[] {
    const flag = this.selectedFlag;
    if (!flag) {
      return this.isPlatformMode ? ['org', 'user'] : ['user'];
    }
    if (!this.isPlatformMode) {
      return flag.scopes.filter((scope) => scope === 'user');
    }
    return [...flag.scopes];
  }

  get showOrgScope(): boolean {
    return this.isPlatformMode && this.availableScopes.includes('org');
  }

  get showUserTarget(): boolean {
    return this.selectedScope === 'user';
  }

  get showOrgTarget(): boolean {
    return this.selectedScope === 'org';
  }

  get allowInherit(): boolean {
    return this.selectedScope === 'user';
  }

  get canApply(): boolean {
    return (
      !!this.selectedFlagKey &&
      !!this.selectedScope &&
      !!this.selectedTargetId &&
      !!this.selectedState &&
      !this.applying
    );
  }

  onFlagChanged(): void {
    const flag = this.selectedFlag;
    if (!flag) {
      return;
    }
    if (flag.key === 'masquerade_as' || !flag.scopes.includes(this.selectedScope)) {
      this.selectedScope = flag.scopes.includes('user') ? 'user' : flag.scopes[0];
    }
    if (this.selectedState === 'inherit' && this.selectedScope !== 'user') {
      this.selectedState = 'enabled';
    }
    this.selectedTargetId = '';
    this.updateConfirmationMessage();
    this.loadAssignments();
  }

  onScopeChanged(): void {
    if (this.selectedFlag?.key === 'masquerade_as') {
      this.selectedScope = 'user';
    }
    if (this.selectedState === 'inherit' && this.selectedScope !== 'user') {
      this.selectedState = 'enabled';
    }
    this.selectedTargetId = '';
    this.updateConfirmationMessage();
    if (this.selectedScope === 'user' && this.isPlatformMode && this.selectedOrgFilterId) {
      this.loadUsersForOrg(this.selectedOrgFilterId);
    }
  }

  onOrgFilterChanged(): void {
    this.selectedTargetId = '';
    if (this.selectedScope === 'user' && this.selectedOrgFilterId) {
      this.loadUsersForOrg(this.selectedOrgFilterId);
    } else if (this.selectedScope === 'user') {
      this.users = [];
      this.filteredUsers = [];
    }
    this.loadAssignments();
  }

  onUserSearchChanged(): void {
    const term = this.userSearch.trim().toLowerCase();
    if (!term) {
      this.filteredUsers = [...this.users];
      return;
    }
    this.filteredUsers = this.users.filter((user) => {
      const haystack = [
        user.email,
        user.name,
        user.surname,
        user.userId,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }

  onStateChanged(): void {
    this.updateConfirmationMessage();
  }

  onTargetChanged(): void {
    this.updateConfirmationMessage();
  }

  apply(): void {
    if (!this.canApply) {
      return;
    }

    const enabled =
      this.selectedState === 'inherit' ? null : this.selectedState === 'enabled';

    this.applying = true;
    this.apiService
      .upsertFeatureFlagAssignment(
        {
          flagKey: this.selectedFlagKey,
          scope: this.selectedScope,
          targetId: this.selectedTargetId,
          enabled,
        }
      )
      .subscribe({
        next: () => {
          this.applying = false;
          this.snackBar.open(this.buildAppliedSnackMessage(enabled), 'Close', { duration: 5000 });
          this.loadAssignments();
        },
        error: (error) => {
          this.applying = false;
          const message = error?.error?.error?.message || 'Failed to update feature flag';
          this.snackBar.open(message, 'Close', { duration: 5000 });
        },
      });
  }

  clearUserOverride(row: AssignmentRow): void {
    if (row.scope !== 'user') {
      return;
    }
    this.applying = true;
    this.apiService
      .upsertFeatureFlagAssignment(
        {
          flagKey: this.selectedFlagKey,
          scope: 'user',
          targetId: row.targetId,
          enabled: null,
        }
      )
      .subscribe({
        next: () => {
          this.applying = false;
          this.snackBar.open(
            'User override cleared — effective value now inherits from org (or OFF if unset).',
            'Close',
            { duration: 5000 }
          );
          this.loadAssignments();
        },
        error: (error) => {
          this.applying = false;
          const message = error?.error?.error?.message || 'Failed to clear user override';
          this.snackBar.open(message, 'Close', { duration: 5000 });
        },
      });
  }

  formatState(enabled: boolean): string {
    return enabled ? 'Enabled' : 'Disabled';
  }

  formatDate(value: string | Date): string {
    if (!value) {
      return '—';
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }
    return date.toLocaleString();
  }

  userLabel(user: UserListItem): string {
    const name = [user.name, user.surname].filter(Boolean).join(' ').trim();
    if (name && user.email) {
      return `${name} (${user.email})`;
    }
    return user.email || name || user.userId;
  }

  private loadRegistry(): void {
    this.loadingRegistry = true;
    this.apiService.listFeatureFlagRegistry().subscribe({
      next: (response) => {
        this.loadingRegistry = false;
        this.registry = response.flags || [];
        if (!this.selectedFlagKey && this.registry.length > 0) {
          this.selectedFlagKey = this.registry[0].key;
          this.onFlagChanged();
        } else if (this.selectedFlagKey) {
          this.onFlagChanged();
        }
      },
      error: (error) => {
        this.loadingRegistry = false;
        const message = error?.error?.error?.message || 'Failed to load feature flag registry';
        this.snackBar.open(message, 'Close', { duration: 5000 });
      },
    });
  }

  private loadOrganizations(): void {
    this.loadingTargets = true;
    this.apiService.listPlatformOrganizations().subscribe({
      next: (response) => {
        this.loadingTargets = false;
        this.organizations = response.organizations || [];
      },
      error: (error) => {
        this.loadingTargets = false;
        const message = error?.error?.error?.message || 'Failed to load organizations';
        this.snackBar.open(message, 'Close', { duration: 5000 });
      },
    });
  }

  private loadUsersForOrg(orgId: string, preferUserId?: string): void {
    if (!orgId) {
      this.users = [];
      this.filteredUsers = [];
      return;
    }

    this.loadingTargets = true;
    this.apiService.listPlatformOrganizationUsers(orgId).subscribe({
      next: (response) => {
        this.loadingTargets = false;
        this.users = response.users || [];
        this.mergeAssignmentUsersIntoPicker(orgId);
        this.onUserSearchChanged();
        if (preferUserId && this.users.some((user) => user.userId === preferUserId)) {
          this.selectedTargetId = preferUserId;
        }
      },
      error: (error) => {
        this.loadingTargets = false;
        this.users = [];
        this.mergeAssignmentUsersIntoPicker(orgId);
        this.onUserSearchChanged();
        if (preferUserId && this.users.some((user) => user.userId === preferUserId)) {
          this.selectedTargetId = preferUserId;
        }
        const message =
          error?.error?.error?.message || 'Failed to load users for that organization';
        this.snackBar.open(message, 'Close', { duration: 5000 });
      },
    });
  }

  private mergeAssignmentUsersIntoPicker(orgId: string): void {
    const existingIds = new Set(this.users.map((user) => user.userId));
    for (const assignment of this.userAssignments) {
      if (assignment.organizationId !== orgId || existingIds.has(assignment.userId)) {
        continue;
      }
      this.users.push({
        userId: assignment.userId,
        email: assignment.email,
        name: assignment.name || undefined,
        surname: assignment.surname || undefined,
        role: 'user',
        enabled: true,
      });
      existingIds.add(assignment.userId);
    }
  }

  private loadAssignments(): void {
    if (!this.selectedFlagKey) {
      this.orgAssignments = [];
      this.userAssignments = [];
      this.assignmentRows.data = [];
      return;
    }

    this.loadingAssignments = true;
    const orgId = this.isPlatformMode ? this.selectedOrgFilterId || undefined : undefined;
    this.apiService
      .listFeatureFlagAssignments(this.selectedFlagKey, {
        orgId,
      })
      .subscribe({
        next: (response) => {
          this.loadingAssignments = false;
          this.orgAssignments = this.isPlatformMode ? response.organizations || [] : [];
          this.userAssignments = response.users || [];
          this.rebuildAssignmentRows();
          if (this.isPlatformMode && this.selectedScope === 'user' && this.selectedOrgFilterId) {
            this.mergeAssignmentUsersIntoPicker(this.selectedOrgFilterId);
            this.onUserSearchChanged();
          }
        },
        error: (error) => {
          this.loadingAssignments = false;
          const message = error?.error?.error?.message || 'Failed to load assignments';
          this.snackBar.open(message, 'Close', { duration: 5000 });
        },
      });
  }

  private rebuildAssignmentRows(): void {
    const rows: AssignmentRow[] = [];

    if (this.isPlatformMode) {
      for (const org of this.orgAssignments) {
        rows.push({
          scope: 'org',
          targetId: org.organizationId,
          targetLabel: org.organizationName || org.organizationId,
          organizationLabel: org.organizationName || org.organizationId,
          enabled: org.enabled,
          updatedAt: org.updatedAt,
        });
      }
    }

    for (const user of this.userAssignments) {
      const name = [user.name, user.surname].filter(Boolean).join(' ').trim();
      rows.push({
        scope: 'user',
        targetId: user.userId,
        targetLabel: name ? `${name} (${user.email || user.userId})` : user.email || user.userId,
        organizationLabel: user.organizationName || user.organizationId,
        enabled: user.enabled,
        updatedAt: user.updatedAt,
      });
    }

    this.assignmentRows.data = rows;
  }

  private updateConfirmationMessage(): void {
    const flagLabel = this.selectedFlag?.label || this.selectedFlagKey || 'this flag';
    if (!this.selectedFlagKey || !this.selectedTargetId) {
      this.confirmationMessage = 'Select a flag, scope, target, and state to preview the effect.';
      return;
    }

    if (this.selectedScope === 'org') {
      if (this.selectedState === 'enabled') {
        this.confirmationMessage =
          `${flagLabel}: organization default ON. Users inherit ON unless they have an override.`;
      } else {
        this.confirmationMessage =
          `${flagLabel}: organization default OFF. Users inherit OFF unless they have an override ON.`;
      }
      return;
    }

    if (this.selectedState === 'inherit') {
      this.confirmationMessage =
        `${flagLabel}: clear user override. Effective value follows the organization (or OFF if the org has no row).`;
      return;
    }

    if (this.selectedState === 'enabled') {
      this.confirmationMessage =
        `${flagLabel}: user override ON. Effective ON even if the organization default is OFF.`;
      return;
    }

    this.confirmationMessage =
      `${flagLabel}: user override OFF. Hard deny — effective OFF even if the organization default is ON.`;
  }

  private buildAppliedSnackMessage(enabled: boolean | null): string {
    if (this.selectedScope === 'org') {
      return enabled
        ? 'Organization flag enabled. Users inherit unless overridden.'
        : 'Organization flag disabled. Users inherit unless overridden.';
    }
    if (enabled === null) {
      return 'User override cleared (inherit from org).';
    }
    if (enabled) {
      return 'User override enabled (spot enable).';
    }
    return 'User override disabled (hard deny if org is ON).';
  }

}
